import { Heading, Separator } from "@overmap-ai/blocks"
import { type AssetType, OrganizationAccessLevel, type Payload } from "@overmap-ai/common"
import { selectAssetTypeById } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import type { ChainedCommands } from "@tiptap/core"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, type JSONContent, useEditor } from "@tiptap/react"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useUserOrganizationAccess } from "../../hooks/users"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import {
	AssetTypeAttachmentNode,
	BASE_EXTENSIONS,
	LINK_NODES,
	MENTION_EXTENSIONS,
	useEditorBlur,
	useEditorContent,
} from "../../tiptap"
import { EditorToolbar, NewLineFloatingMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { AssetTypeFileHandler } from "../../tiptap/extensions"
import { BaseAttachmentNode } from "../../tiptap/nodes/BaseAttachment/extension"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/asset-types/$assetTypeId/general")

export const AssetTypeSettingsGeneralView = memo(() => {
	const { assetTypeId } = routeApi.useParams()

	const assetType = useAppSelector(selectAssetTypeById(assetTypeId))

	if (!assetType) {
		throw new Error(`expected asset type to be defined, got ${assetType}`)
	}

	const isOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handlePatchAssetType = useCallback(
		(payload: Partial<Payload<AssetType>>) => {
			void sdk.assetTypes.update({ uuid: assetType.uuid, ...payload })
		},
		[assetType.uuid],
	)

	const handleDescriptionChange = useCallback(
		(description: JSONContent | undefined) => {
			handlePatchAssetType({ description: description ? JSON.stringify(description) : undefined })
		},
		[handlePatchAssetType],
	)

	const handleAttachFilesToAssetType = useCallback(
		(files: File[]) => {
			return sdk.assetTypeAttachments.bulkAdd(
				files.map((file) => ({
					asset_type: assetType.uuid,
					file,
				})),
			)
		},
		[assetType.uuid],
	)

	const editorContent = useEditorContent(assetType.description)
	const handleEditorBlur = useEditorBlur(editorContent, handleDescriptionChange)

	const editor = useEditor(
		{
			editorProps: {
				attributes: { class: "outline-none" },
			},
			content: editorContent,
			editable: isOrganizationAdmin,
			onBlur: handleEditorBlur,
			extensions: [
				...BASE_EXTENSIONS,
				...MENTION_EXTENSIONS,
				...LINK_NODES,
				AssetTypeAttachmentNode,
				AssetTypeFileHandler.configure({
					uuid: assetType.uuid,
				}),
				Placeholder.configure({
					placeholder: "Enter description",
				}),
			],
		},
		[assetType.uuid, isOrganizationAdmin, handlePatchAssetType],
	)

	const handleInsertAttachments = useCallback(
		(chainedCommands: () => ChainedCommands, files: File[]) => {
			void handleAttachFilesToAssetType(files).then(([offlineAttachments]) => {
				chainedCommands()
					.insertContent(
						offlineAttachments.map((offlineAttachment) => ({
							type: BaseAttachmentNode.name,
							attrs: {
								uuid: offlineAttachment.uuid,
							},
						})),
					)
					.run()
			})
		},
		[handleAttachFilesToAssetType],
	)

	return (
		<ViewContainer maxWidth="6xl">
			<section className="flex size-full flex-col gap-4">
				<Heading size="xl">General</Heading>

				<Separator size="full" />

				<div className="flex size-full flex-col gap-4 overflow-hidden">
					<EditorContext value={editor}>
						<EditorToolbar editor={editor} />
						<EditorContent className="w-full grow overflow-y-auto overflow-x-hidden" editor={editor} />
						<NewLineFloatingMenu editor={editor} onAttachFiles={handleInsertAttachments} />
					</EditorContext>
				</div>
			</section>
		</ViewContainer>
	)
})
AssetTypeSettingsGeneralView.displayName = "AssetTypeSettingsGeneralView"
